import { useAllUOMs } from "http-hooks/uom/useUOM";
import DeleteUOM from "components/uom/DeleteUOM";
import { useState } from "react";
import Modal from "components/ui/modals";
import useModalStore from "store/modals";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
  ActionButton,
} from "components/ui/Table";
import Select from "components/ui/Select";
import Pagination from "components/ui/Pagination";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Constants moved to top to prevent re-renders
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const initialFilter = {
  itemuom: "",
  itembasicuom: "",
  ag_status: "",
  page: 1,
  limit: 10,
};

export default function UomConversion() {
  const [deleteUomId, setDeleteUomId] = useState(null);
  const [filter, setFilter] = useState(initialFilter);
  const [selectedItemUom, setSelectedItemUom] = useState(null);
  const [selectedItemBasicUom, setSelectedItemBasicUom] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const { openModal } = useModalStore();

  const openSetModal = (id) => {
    openModal("uom", id);
  };

  const handleItemUomChange = (selectedOption) => {
    setSelectedItemUom(selectedOption);
    setFilter((prev) => ({
      ...prev,
      itemuom: selectedOption?.value || "",
      page: 1, // Reset to first page when filtering
    }));
  };

  const handleItemBasicUomChange = (selectedOption) => {
    setSelectedItemBasicUom(selectedOption);
    setFilter((prev) => ({
      ...prev,
      itembasicuom: selectedOption?.value || "",
      page: 1, // Reset to first page when filtering
    }));
  };

  const handleStatusChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setFilter((prev) => ({
      ...prev,
      ag_status: selectedOption?.value || "",
      page: 1, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (page) => {
    setFilter((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const clearFilters = () => {
    setSelectedItemUom(null);
    setSelectedItemBasicUom(null);
    setSelectedStatus(null);
    setFilter(initialFilter);
  };

  const {
    isLoading: isAllUOMsLoading,
    data: { data: allUOMs, page: currentPage, totalPages } = [],
  } = useAllUOMs(filter);

  // Create unique options for dropdowns
  const uniqueItemUomOptions = Array.from(
    new Set(allUOMs?.map((uom) => uom.itemuom) || [])
  ).map((itemuom) => ({
    value: itemuom,
    label: itemuom,
  }));

  const uniqueItemBasicUomOptions = Array.from(
    new Set(allUOMs?.map((uom) => uom.itembasicuom) || [])
  ).map((itembasicuom) => ({
    value: itembasicuom,
    label: itembasicuom,
  }));

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {deleteUomId && (
        <Modal
          open={!!deleteUomId}
          setOpen={(val) => !val && setDeleteUomId(null)}
        >
          <DeleteUOM id={deleteUomId} onClose={() => setDeleteUomId(null)} />
        </Modal>
      )}

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            UOM Conversion
          </h1>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => openSetModal()}
            className="block rounded-md bg-gray-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          >
            Add UOM conversion
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-lg p-3">
          <Select
            options={uniqueItemUomOptions}
            value={selectedItemUom}
            onChange={handleItemUomChange}
            label="Item UOM"
            placeholder="Select Item UOM"
            isClearable={true}
            isSearchable={true}
          />
        </div>
        <div className="bg-white rounded-lg p-3">
          <Select
            options={uniqueItemBasicUomOptions}
            value={selectedItemBasicUom}
            onChange={handleItemBasicUomChange}
            label="Item Basic UOM"
            placeholder="Select Item Basic UOM"
            isClearable={true}
            isSearchable={true}
          />
        </div>
        <div className="bg-white rounded-lg p-3">
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            label="Status"
            placeholder="Select status"
            isClearable={true}
            isSearchable={false}
          />
        </div>
        <div className="bg-white rounded-lg p-3 flex items-end">
          <button
            type="button"
            onClick={clearFilters}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 flow-root">
        <Table>
          <THead>
            <TR>
              <TH first>Item UOM</TH>
              <TH>Conversion Factor</TH>
              <TH>Item Basic UOM</TH>
              <TH>Status</TH>
              <TH last>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {isAllUOMsLoading ? (
              <LoadingRow colSpan={5} message="Loading UOM conversions..." />
            ) : allUOMs?.length === 0 ? (
              <EmptyRow colSpan={5} message="No UOM conversions found." />
            ) : (
              allUOMs?.map((uom) => (
                <TR key={uom.uomconvid}>
                  <TD first>{uom.itemuom}</TD>
                  <TD>{uom.conval}</TD>
                  <TD>{uom.itembasicuom}</TD>
                  <TD>
                    {uom.ag_status === 1 ? (
                      <CheckIcon
                        className="h-5 w-5 text-green-600"
                        aria-hidden="true"
                      />
                    ) : (
                      <XMarkIcon
                        className="h-5 w-5 text-red-600"
                        aria-hidden="true"
                      />
                    )}
                  </TD>
                  <TD last>
                    <ActionButton
                      variant="primary"
                      onClick={() => openSetModal(uom.uomconvid)}
                      title="Edit UOM Conversion"
                    >
                      <PencilSquareIcon
                        className="h-4 w-4"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Edit UOM conversion</span>
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => setDeleteUomId(uom.uomconvid)}
                      title="Delete UOM Conversion"
                      className="mr-0"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Delete UOM conversion</span>
                    </ActionButton>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPageJump={true}
      />
    </div>
  );
}
